import { usePrivateMutation as useMutation } from "@/lib/query/core/use-private-mutation";
import { useQueryClient } from "@tanstack/react-query";
import type { AdminTagManagerView } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";

type SetTagManagerDisabledInput = {
  storeId: string;
  disabled: boolean;
  /** Internal note for the next admin. Never shown to the seller or a buyer. */
  reason?: string | null;
};

export const storeTagManagerQueryKey = (storeId: string) =>
  ["admin", "store-tag-manager", storeId] as const;

/**
 * Withdraw (or restore) a store's ability to run a Tag Manager container.
 *
 * The error is deliberately NOT swallowed here: a 403 from the guard, or a
 * failure to write, has to reach the card so the toggle can snap back. A kill
 * switch that renders as "off" when the request was refused is worse than one
 * that does not exist, because somebody stops looking.
 */
export function useSetTagManagerDisabledMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ storeId, disabled, reason }: SetTagManagerDisabledInput) =>
      apiJsonClient<AdminTagManagerView>(
        endpoints.admin.storeTagManager(storeId),
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ disabled, reason: reason ?? null }),
        },
      ),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(storeTagManagerQueryKey(variables.storeId), data);
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
